import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  ArrowLeft, Loader2, Building2, FileText, Mail, Phone, MapPin, Plus, ChevronRight, TrendingUp, Wallet, AlertCircle,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { formatIndianDate, formatCurrencyINR } from "../utils/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("invoices");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/customers/${customerId}/activity`, { withCredentials: true }),
      axios.get(`${API}/customers/${customerId}/financial`, { withCredentials: true }),
    ])
      .then(([a, b]) => { setData(a.data); setFinancial(b.data); })
      .catch(err => {
        toast.error(err.response?.data?.detail || "Failed to load customer");
        navigate("/customers");
      })
      .finally(() => setLoading(false));
  }, [customerId, navigate]);

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  }
  if (!data) return null;

  const c = data.customer;
  const invoices = data.invoices || [];
  const quotations = data.quotations || [];
  const sales = invoices.filter(i => i.invoice_type !== "purchase");

  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-8 px-4" data-testid="customer-detail-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/customers" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Customers
        </Link>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 sm:p-6 mb-5"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-start">
            <div className="flex gap-3 items-start">
              <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="cust-name">
                  {c.name}
                </h1>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500 mt-1">
                  {c.gstin && <span className="font-mono px-2 py-0.5 bg-slate-100 rounded">{c.gstin}</span>}
                  {c.state && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{c.state}</span>}
                  {c.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                  {c.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
                </div>
                {c.address && <p className="text-xs text-slate-500 mt-1">{c.address}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link to={`/create?customer_id=${c.customer_id}`}>
                <Button variant="outline" className="text-sm" data-testid="cust-new-invoice">
                  <Plus className="w-4 h-4 mr-1" /> New Invoice
                </Button>
              </Link>
              <Link to={`/quotations/new?customer_id=${c.customer_id}`}>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm" data-testid="cust-new-quote">
                  <Plus className="w-4 h-4 mr-1" /> New Quotation
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>

        {/* Financial summary */}
        {financial && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="cust-financial">
            <KPI label="Total Sales" value={formatCurrencyINR(financial.total_sales)} icon={TrendingUp} tone="blue" testid="kpi-sales" />
            <KPI label="Paid" value={formatCurrencyINR(financial.total_paid)} icon={Wallet} tone="emerald" testid="kpi-paid" />
            <KPI label="Outstanding" value={formatCurrencyINR(financial.outstanding)} icon={Wallet} tone="amber" testid="kpi-outstanding" />
            <KPI label="Overdue" value={formatCurrencyINR(financial.overdue)} icon={AlertCircle} tone="red" testid="kpi-overdue" />
            <KPI label="Documents" value={`${financial.invoice_count} inv · ${financial.quotation_count} qt`} icon={FileText} tone="violet" testid="kpi-docs" />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="flex border-b border-slate-100">
            {[
              { id: "invoices", label: `Invoices (${sales.length})` },
              { id: "quotations", label: `Quotations (${quotations.length})` },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`cust-tab-${t.id}`}
                className={`flex-1 sm:flex-initial px-4 sm:px-6 py-3 text-sm font-semibold transition-colors ${
                  tab === t.id ? "text-blue-700 border-b-2 border-blue-600" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "invoices" && (
            <div data-testid="cust-invoices-list">
              {sales.length === 0 ? (
                <EmptyMini text="No invoices for this client yet." cta={{ to: `/create?customer_id=${c.customer_id}`, label: "Create Invoice" }} />
              ) : sales.map(inv => (
                <Link
                  key={inv.invoice_id}
                  to={`/invoices/${inv.invoice_id}`}
                  className="flex items-center gap-3 p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                  data-testid={`cust-inv-${inv.invoice_id}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2">
                      <span className="font-semibold text-slate-900 text-sm">{inv.invoice_number}</span>
                      <PaymentBadge status={inv.payment_status} />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatIndianDate(inv.invoice_date)}
                      {inv.due_date && <> · Due {formatIndianDate(inv.due_date)}</>}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-slate-900">{formatCurrencyINR(inv.grand_total)}</p>
                    <p className="text-[11px] text-emerald-600">{formatCurrencyINR(inv.amount_paid || 0)} paid</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}

          {tab === "quotations" && (
            <div data-testid="cust-quotations-list">
              {quotations.length === 0 ? (
                <EmptyMini text="No quotations for this client yet." cta={{ to: `/quotations/new?customer_id=${c.customer_id}`, label: "Create Quotation" }} />
              ) : quotations.map(q => (
                <Link
                  key={q.quotation_id}
                  to={`/quotations/${q.quotation_id}`}
                  className="flex items-center gap-3 p-4 border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                  data-testid={`cust-quote-${q.quotation_id}`}
                >
                  <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2">
                      <span className="font-semibold text-slate-900 text-sm">{q.quotation_number}</span>
                      <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{q.status?.replace(/_/g, " ")}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatIndianDate(q.quotation_date)}
                      {q.valid_until && <> · Valid until {formatIndianDate(q.valid_until)}</>}
                    </p>
                  </div>
                  <p className="text-sm font-bold text-slate-900">{formatCurrencyINR(q.grand_total)}</p>
                  <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, tone, testid }) {
  const toneCls = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    violet: "bg-violet-50 text-violet-700",
  }[tone];
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`w-6 h-6 rounded-lg flex items-center justify-center ${toneCls}`}><Icon className="w-3.5 h-3.5" /></span>
        <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{label}</p>
      </div>
      <p className="text-sm sm:text-base font-bold text-slate-900 truncate">{value}</p>
    </div>
  );
}

function PaymentBadge({ status }) {
  const map = {
    paid: { label: "Paid", cls: "bg-emerald-50 text-emerald-700" },
    partial: { label: "Partial", cls: "bg-amber-50 text-amber-700" },
    unpaid: { label: "Unpaid", cls: "bg-red-50 text-red-700" },
  }[status] || { label: status, cls: "bg-slate-100 text-slate-700" };
  return <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${map.cls}`}>{map.label}</span>;
}

function EmptyMini({ text, cta }) {
  return (
    <div className="text-center py-10 px-5">
      <FileText className="w-8 h-8 text-slate-300 mx-auto mb-2" />
      <p className="text-sm text-slate-500 mb-3">{text}</p>
      {cta && (
        <Link to={cta.to}>
          <Button className="bg-blue-600 hover:bg-blue-700 text-white text-sm">
            <Plus className="w-3.5 h-3.5 mr-1" /> {cta.label}
          </Button>
        </Link>
      )}
    </div>
  );
}
